/**
 * Wishlist — Swym Wishlist Plus (feature 7, Voie A / JS SDK). Single source of truth for the state of
 * every JD heart button (`[data-jd-wishlist]`) across the store. Replaces the three former mock
 * toggles (jd-pdp.js click flip, jd-featured-collection.liquid localStorage, jd-cart.js flip).
 *
 * Swym injects its SDK sitewide once the Wishlist Plus app is installed (Premium plan). We drive our
 * own custom hearts through the documented JS SDK (`window.SwymCallbacks` → `swat`): fetch the
 * visitor's list, add/remove, and mirror the state on every rendered instance of a product. No API
 * key in the theme — the SDK carries its own auth.
 *
 * A heart's product object is read from its data-* attributes:
 *   empi = data-product-id · epi = data-variant-id · du = data-product-url (absolute).
 * State is keyed per variant (`empi:epi`), so a card shows the state of its own variant and the PDP
 * heart follows the selected one.
 *
 * Degradation: if the SDK never loads (app/plan absent) every public method resolves to a no-op and
 * the hearts stay inert — no JS error. `window.jdWishlist` is the public API for other modules
 * (e.g. the cart remove modal): { add, remove, toggle, has, ready }.
 */
(function () {
  'use strict';

  const SELECTOR = '[data-jd-wishlist]';

  const state = {
    swat: null,
    lid: null, // list the hearts write to
    items: new Map(), // `${empi}:${epi}` -> lid of the list holding it
  };

  const keyOf = (empi, epi) => `${empi}:${epi}`;

  // `fetchLists` exposes no "default list" flag and returns the lists newest-first, so reading
  // lists[0] flips targets as soon as a visitor owns more than one — hearts written to one list,
  // read back from the other. Pick the oldest wishlist: stable across page loads.
  function pickList(lists) {
    return (
      (lists || [])
        .filter((list) => list && list.lid && (!list.lty || list.lty === 'wl'))
        .sort((a, b) => (a.cts || 0) - (b.cts || 0))[0] || null
    );
  }

  // Index every list, not just the one we write to: a visitor who used Swym's own UI has items in
  // the list it created, and a heart must show them as saved.
  function indexLists(lists) {
    state.items.clear();
    (lists || []).forEach((list) => {
      (list.listcontents || []).forEach((item) => state.items.set(keyOf(item.empi, item.epi), list.lid));
    });
  }

  // The name Swym itself gives the list it creates — not the app's `Title` setting, which is the
  // drawer's (localized) heading. Creating ours under any other name splits the wishlist in two:
  // our hearts on one list, Swym's own drawer on the other.
  const LIST_NAME = 'My Wishlist';

  function productFrom(el) {
    const empi = el.getAttribute('data-product-id');
    const epi = el.getAttribute('data-variant-id');
    if (!empi || !epi) return null;
    return {
      empi: String(empi),
      epi: String(epi),
      du: el.getAttribute('data-product-url') || window.location.href,
    };
  }

  // Reflect the cached state onto the hearts under `root` (whole document by default).
  function reflect(root) {
    (root || document).querySelectorAll(SELECTOR).forEach((el) => {
      const p = productFrom(el);
      if (!p) return;
      el.setAttribute('aria-pressed', state.items.has(keyOf(p.empi, p.epi)) ? 'true' : 'false');
    });
  }

  // Set every heart of a given product+variant (cards + PDP) to `on`.
  function setPressed(product, on) {
    document
      .querySelectorAll(`${SELECTOR}[data-product-id="${product.empi}"]`)
      .forEach((el) => {
        if (el.getAttribute('data-variant-id') === product.epi) {
          el.setAttribute('aria-pressed', on ? 'true' : 'false');
        }
      });
  }

  // --- ready promise: resolves once Swym has answered, or after a timeout when the SDK is absent ---
  let resolveReady;
  const readyPromise = new Promise((r) => (resolveReady = r));
  const settleReady = () => resolveReady();
  window.setTimeout(settleReady, 8000); // degradation guard: never hang if the SDK never boots

  // --- Swym boot ---
  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(function (swat) {
    state.swat = swat;
    if (!swat || typeof swat.fetchLists !== 'function') {
      settleReady();
      return;
    }

    swat.fetchLists({
      callbackFn: function (lists) {
        const list = pickList(lists);
        if (list) state.lid = list.lid;
        indexLists(lists);
        reflect();
        settleReady();
      },
      errorFn: settleReady,
    });

    // Cross-tab / Swym's own UI: keep our hearts in sync when the list changes elsewhere.
    const evts = swat.JSEvents || {};
    if (evts.addedToWishlist) document.addEventListener(evts.addedToWishlist, onSwymChange(true));
    if (evts.removedFromWishlist) document.addEventListener(evts.removedFromWishlist, onSwymChange(false));
  });

  function onSwymChange(added) {
    return function (event) {
      const d = (event && event.detail && (event.detail.d || event.detail)) || {};
      if (d.empi == null || d.epi == null) return;
      const key = keyOf(d.empi, d.epi);
      if (added) state.items.set(key, d.lid || state.lid);
      else state.items.delete(key);
      reflect();
    };
  }

  // Resolve (and cache) the list the hearts write to. Re-fetch when unknown rather than trusting the
  // boot-time snapshot — a list may have appeared since (Swym's own UI, another tab) — and only
  // create one when the account genuinely has none, so we never spawn duplicate lists.
  function resolveList() {
    return new Promise((resolve) => {
      if (state.lid) return resolve(state.lid);
      const swat = state.swat;
      if (!swat || typeof swat.fetchLists !== 'function') return resolve(null);
      swat.fetchLists({
        callbackFn: (lists) => {
          const list = pickList(lists);
          if (list) {
            state.lid = list.lid;
            indexLists(lists);
            return resolve(state.lid);
          }
          if (typeof swat.createList !== 'function') return resolve(null);
          swat.createList(
            { lname: LIST_NAME },
            (created) => {
              state.lid = created && created.lid;
              resolve(state.lid);
            },
            () => resolve(null)
          );
        },
        errorFn: () => resolve(null),
      });
    });
  }

  // Swym answers some calls with a transient failure (its product-view instrumentation registers a
  // store's products asynchronously); the failed call itself nudges it along, so a short retry then
  // succeeds — the same "click again and it sticks" behaviour, automated. Swym also fails on some
  // products in a way retrying does not recover (HTTP 200 carrying `hasError`, "…because this.text
  // is null"): a server-side product resolution bug, nothing the theme can work around.
  function swymCallWithRetry(run) {
    const DELAYS = [0, 800, 1500, 2500]; // 4 attempts across ~4.8s
    return new Promise((resolve) => {
      let attempt = 0;
      const tryOnce = () => {
        run(
          () => resolve(true),
          () => {
            attempt += 1;
            if (attempt >= DELAYS.length) return resolve(false);
            window.setTimeout(tryOnce, DELAYS[attempt]);
          }
        );
      };
      tryOnce();
    });
  }

  async function add(product) {
    if (!product) return false;
    const swat = state.swat;
    const lid = await resolveList();
    if (!swat || typeof swat.addToList !== 'function' || !lid) return false;
    const ok = await swymCallWithRetry((onSuccess, onError) =>
      swat.addToList(lid, product, onSuccess, onError)
    );
    if (ok) {
      state.items.set(keyOf(product.empi, product.epi), lid);
      setPressed(product, true);
    }
    return ok;
  }

  async function remove(product) {
    if (!product) return false;
    const swat = state.swat;
    // Remove from the list that actually holds the item — which is not always the one we write to
    // when the visitor also has a list created by Swym's own UI.
    const lid = state.items.get(keyOf(product.empi, product.epi)) || (await resolveList());
    if (!swat || typeof swat.deleteFromList !== 'function' || !lid) return false;
    const ok = await swymCallWithRetry((onSuccess, onError) =>
      swat.deleteFromList(lid, product, onSuccess, onError)
    );
    if (ok) {
      state.items.delete(keyOf(product.empi, product.epi));
      setPressed(product, false);
    }
    return ok;
  }

  function has(product) {
    return !!product && state.items.has(keyOf(product.empi, product.epi));
  }

  // Swym renders its add/remove toast in a `position: fixed` wrapper carrying a maxed-out z-index —
  // and it is still painted *under* any modal <dialog>, which renders in the top layer, out of
  // z-index's reach. The predictive search overlay is such a dialog, so a heart clicked from its
  // results toasted invisibly behind it.
  //
  // The way into the top layer without being a dialog is the popover API. Reparenting the toast into
  // the open dialog would also work on paper, but not here: the search modal keeps a
  // `transform: translateY(0)` after its slide-in (animation-fill-mode: forwards), which makes it the
  // containing block of any fixed descendant — the toast would be positioned against the panel and
  // clipped by its `overflow: hidden`. A popover stays a child of Swym's own root, so their
  // positioning keeps resolving against the viewport.
  //
  // Re-opened on each interaction rather than left open: the top layer is ordered by open time, so a
  // popover opened before the dialog would sit under it.
  const TOAST_WRAPPER = '[class*="swym-storefront-layout-notification-position-"]';

  function raiseToastAboveModals() {
    const toast = document.querySelector(TOAST_WRAPPER);
    if (!toast || typeof toast.showPopover !== 'function') return; // layout UI not booted / no support
    if (!document.querySelector('dialog:modal')) return; // nothing to clear — leave Swym alone
    if (!toast.hasAttribute('popover')) toast.setAttribute('popover', 'manual');
    try {
      if (toast.matches(':popover-open')) toast.hidePopover();
      toast.showPopover();
    } catch (e) {
      /* detached mid-flight: the toast simply stays where Swym put it */
    }
  }

  // --- delegated heart clicks (cards + PDP) ---
  document.addEventListener('click', async (event) => {
    const btn = event.target.closest(SELECTOR);
    if (!btn) return;
    event.preventDefault();

    const product = productFrom(btn);
    if (!product) return;

    const wasIn = btn.getAttribute('aria-pressed') === 'true';
    setPressed(product, !wasIn); // optimistic, on every instance of this variant
    raiseToastAboveModals(); // before the call: the toast can land as soon as the server answers
    const ok = wasIn ? await remove(product) : await add(product);
    raiseToastAboveModals(); // and after, for the first interaction of a session
    if (!ok) setPressed(product, wasIn); // revert on failure / degraded SDK
  });

  // Sections injected late (recently-viewed, carousels, filtered grids) re-emit shopify:section:load
  // → re-mark their hearts from the cached state. (Takes over the removed mock's re-sync role.)
  document.addEventListener('shopify:section:load', (event) => reflect(event.target));

  // --- public API ---
  window.jdWishlist = {
    add,
    remove,
    toggle: (product) => (has(product) ? remove(product) : add(product)),
    has,
    ready: readyPromise,
  };
})();
